import { Menu, Search, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { EVENTS_PAGE_ENABLED, NAV_ITEMS } from '@/config/site';

const VISIBLE_NAV_ITEMS = NAV_ITEMS.filter(
  (item) => EVENTS_PAGE_ENABLED || item.to !== '/programajanlo'
);

interface NavbarProps {
  /** Menu path to highlight in addition to the current URL (e.g. an article's category page). */
  activePath?: string;
}

export const Navbar: React.FC<NavbarProps> = ({ activePath }) => {
  const [isOpen, setIsOpen] = useState(false);
  // The bar stays on screen (sticky) and gets slimmer once the page is scrolled
  const [compact, setCompact] = useState(false);
  const location = useLocation();

  const toggleRef = useRef<HTMLButtonElement>(null);

  const isActive = (to: string) => location.pathname === to || activePath === to;
  const closeMenu = () => setIsOpen(false);

  // While the mobile menu is open: the page behind it doesn't scroll, Esc closes it (focus goes
  // back to the toggle button), and it closes when the window grows to the desktop layout
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);

    const desktop = window.matchMedia?.('(min-width: 768px)');
    const onDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setIsOpen(false);
    };
    desktop?.addEventListener?.('change', onDesktop);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      desktop?.removeEventListener?.('change', onDesktop);
    };
  }, [isOpen]);

  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`sticky top-0 w-full z-50 border-b border-neutral-800 transition-colors duration-300 ${
        compact ? 'bg-black/90 backdrop-blur-md' : 'bg-black'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div
          className={`flex items-center justify-between transition-[height] duration-300 ease-smooth ${
            compact ? 'h-14' : 'h-20'
          }`}
        >
          {/* Logo */}
          <Link
            to="/"
            onClick={closeMenu}
            className="flex-shrink-0 flex flex-col justify-center cursor-pointer group"
          >
            <span className="font-sans font-black text-2xl tracking-tighter text-white md:group-hover:text-neutral-300 transition-colors duration-150 leading-none">
              HARD NEWS <span className="text-neon-red">HUNGARY</span>
            </span>
            <span
              className={`text-[10px] font-mono text-neutral-400 tracking-[0.3em] uppercase overflow-hidden md:group-hover:text-white transition-all duration-300 ${
                compact ? 'max-h-0 opacity-0' : 'max-h-4 mt-1'
              }`}
            >
              Go Hard or Go Home
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-2">
              {VISIBLE_NAV_ITEMS.map((item) => {
                const active = isActive(item.to);
                const classes =
                  'relative group px-4 py-2 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black';
                const content = (
                  <>
                    {/* Background slide effect */}
                    <div
                      className={`
                      absolute inset-0 bg-neutral-800
                      transform origin-bottom -skew-x-12
                      transition-transform duration-300 ease-smooth
                      ${active ? 'translate-y-0 opacity-20' : 'translate-y-full group-hover:translate-y-0'}
                    `}
                    />

                    {/* Text */}
                    <span
                      className={`
                      relative z-10 font-mono text-sm font-bold uppercase tracking-widest block
                      transition-colors duration-150
                      ${active ? 'text-neon-red' : 'text-neutral-400 group-hover:text-neon-red'}
                    `}
                    >
                      {item.label}
                    </span>

                    {/* Active Indicator */}
                    {active && (
                      <span className="absolute bottom-0 left-0 w-full h-[2px] bg-neon-red shadow-glow-sm"></span>
                    )}

                    {/* Corner accents on hover */}
                    <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-neon-red opacity-0 group-hover:opacity-100 transition-opacity duration-150"></span>
                    <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-neon-red opacity-0 group-hover:opacity-100 transition-opacity duration-150"></span>
                  </>
                );

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={closeMenu}
                    className={classes}
                    aria-current={active ? 'page' : undefined}
                  >
                    {content}
                  </Link>
                );
              })}
              <Link
                to="/kereses"
                aria-label="Keresés"
                title="Keresés"
                className={`self-center p-2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                  location.pathname === '/kereses'
                    ? 'text-neon-red'
                    : 'text-neutral-400 hover:text-neon-red'
                }`}
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Mobile search + menu buttons */}
          <div className="md:hidden flex items-center gap-1">
            <Link
              to="/kereses"
              onClick={closeMenu}
              aria-label="Keresés"
              className="text-neutral-400 hover:text-neon-red p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-colors duration-150"
            >
              <Search className="h-7 w-7" aria-hidden="true" />
            </Link>
            <button
              ref={toggleRef}
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              className="text-neutral-400 hover:text-neon-red p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-colors duration-150"
              aria-label={isOpen ? 'Menü bezárása' : 'Menü megnyitása'}
            >
              {isOpen ? (
                <X className="h-8 w-8" aria-hidden="true" />
              ) : (
                <Menu className="h-8 w-8" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu: covers the page below the navbar */}
      {isOpen && (
        <div
          id="mobile-menu"
          className={`md:hidden absolute inset-x-0 top-full overflow-y-auto overscroll-contain bg-black border-b border-neutral-800 motion-safe:animate-fade-in ${
            compact ? 'h-[calc(100dvh-3.5rem)]' : 'h-[calc(100dvh-5rem)]'
          }`}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {VISIBLE_NAV_ITEMS.map((item) => {
              const active = isActive(item.to);
              const classes = `
                block w-full text-left px-3 py-4 text-base font-bold font-mono border-l-4 transition-all duration-150 uppercase tracking-widest
                focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-inset
                ${
                  active
                    ? 'border-neon-red text-neon-red bg-neutral-900 shadow-glow-sm'
                    : 'border-transparent text-neutral-400 hover:border-neon-red/50 hover:text-neon-red'
                }
              `;

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  className={classes}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};
